import { Switch } from '@/components/ui/switch';
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Control, FieldPath, FieldValues } from 'react-hook-form';

interface FormSwitchBaseProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
	/** React Hook Form control */
	control: Control<TFieldValues>;
	/** Field name */
	name: TName;
	/** Label text */
	label: string;
}

interface InputStyleFormSwitchProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends FormSwitchBaseProps<TFieldValues, TName> {
	/** Input style - resembles other form inputs */
	variant: 'input';
	/** Text to show when enabled */
	enabledText: string;
	/** Text to show when disabled */
	disabledText: string;
}

interface InlineFormSwitchProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends FormSwitchBaseProps<TFieldValues, TName> {
	/** Inline style - switch with label beside it */
	variant: 'inline';
	/** Text to show when enabled (optional) */
	enabledText?: string;
	/** Text to show when disabled (optional) */
	disabledText?: string;
	/** Width for the status text (for alignment) */
	statusTextWidth?: string;
}

interface CompactFormSwitchProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends FormSwitchBaseProps<TFieldValues, TName> {
	/** Compact style - just switch with label, no status text */
	variant: 'compact';
	/** Additional class name for FormItem */
	className?: string;
}

type FormSwitchProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> =
	| InputStyleFormSwitchProps<TFieldValues, TName>
	| InlineFormSwitchProps<TFieldValues, TName>
	| CompactFormSwitchProps<TFieldValues, TName>;

/**
 * Shared form switch component with consistent styling across all forms.
 *
 * Variants:
 * - `input`: Styled like form inputs with label above and status text inside
 * - `inline`: Switch with label and optional status text beside it
 * - `compact`: Simple switch with label, no status text
 *
 * @example
 * // Input style (like VIP access, Auto renew)
 * <FormSwitch
 *   control={form.control}
 *   name="isVip"
 *   variant="input"
 *   label="VIP Access"
 *   enabledText="Enabled"
 *   disabledText="Disabled"
 * />
 *
 * @example
 * // Inline style (like marketing opt-in)
 * <FormSwitch
 *   control={form.control}
 *   name="marketingOptIn"
 *   variant="inline"
 *   label="Marketing"
 *   enabledText="Subscribed"
 *   disabledText="Unsubscribed"
 * />
 *
 * @example
 * // Compact style (like isPrimary in field lists)
 * <FormSwitch
 *   control={form.control}
 *   name="isPrimary"
 *   variant="compact"
 *   label="Mark as primary"
 * />
 */
export function FormSwitch<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: FormSwitchProps<TFieldValues, TName>) {
	const { control, name, label, variant } = props;

	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => {
				if (variant === 'input') {
					const { enabledText, disabledText } = props as InputStyleFormSwitchProps<TFieldValues, TName>;
					return (
						<FormItem>
							<FormLabel>{label}</FormLabel>
							<FormControl>
								<div
									className="flex items-center justify-between h-9 px-3 rounded-md border border-input bg-[#fafafa] dark:bg-slate-900 cursor-pointer"
									onClick={() => field.onChange(!field.value)}
								>
									<span
										className={
											field.value
												? 'text-sm font-semibold text-foreground'
												: 'text-sm text-muted-foreground'
										}
									>
										{field.value ? enabledText : disabledText}
									</span>
									<div onClick={(e) => e.stopPropagation()}>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</div>
								</div>
							</FormControl>
							<FormMessage />
						</FormItem>
					);
				}

				if (variant === 'inline') {
					const { enabledText, disabledText, statusTextWidth } = props as InlineFormSwitchProps<TFieldValues, TName>;
					const hasStatusText = enabledText && disabledText;
					return (
						<FormItem className="flex items-center gap-3">
							<FormLabel className="font-medium">{label}</FormLabel>
							<FormControl>
								<div className="flex items-center gap-2">
									<Switch
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
									{hasStatusText && (
										<span
											className={`text-sm ${statusTextWidth || ''} ${
												field.value
													? 'font-semibold text-foreground'
													: 'text-muted-foreground'
											}`}
										>
											{field.value ? enabledText : disabledText}
										</span>
									)}
								</div>
							</FormControl>
						</FormItem>
					);
				}

				// Compact variant
				const { className } = props as CompactFormSwitchProps<TFieldValues, TName>;
				return (
					<FormItem className={`flex items-center gap-2 ${className || ''}`}>
						<FormControl>
							<Switch
								checked={Boolean(field.value)}
								onCheckedChange={field.onChange}
							/>
						</FormControl>
						<FormLabel className="font-normal">{label}</FormLabel>
					</FormItem>
				);
			}}
		/>
	);
}
